/* eslint-disable @typescript-eslint/naming-convention */
import { AlertLooks, CircleLoader, SearchTextfield, StandardAlert, StandardSwitch } from '@brandfolder/react';
import React, { useState, FunctionComponent, ReactNode } from 'react';
import { decode } from 'html-entities';

import { fetchJson } from '@api/ApiHelper';
import { Locale } from '@components/common/language_menu/languagesMap';
import { getPageSize, getNumPages, RowPage, getPageSlice } from '@components/common/shroud/row_page';
import { genericError } from '@components/standard-messages';
import {
  BulkCustomFieldsLanguageMenuDropdown
} from '@components/bulk_management/user_generated_translations/BulkManagementLanguageMenuDropdown';
import { ListDropdown } from '@components/library/dropdown';
import {
  sortedNames,
  updateVisibleTags,
  TagCountResponse,
  TagSortByOptions
} from '@components/bulk_management/tags/helpers';
import TagsRow from '@components/bulk_management/tags/tags_row';

import './styles/main.scss';

export interface TagsManagementProps {
  autoGenerated: boolean;
  error: boolean;
  loading: boolean;
  setAutoGenerated: SetStateDispatch<boolean>;
  setTags: SetStateDispatch<TagCountResponse>;
  setUgtLocale: SetStateDispatch<Locale>;
  tags: TagCountResponse;
  ugtLocale: Locale;
}

export const TagsManagement: FunctionComponent<TagsManagementProps> = ({
  autoGenerated,
  error,
  loading,
  setAutoGenerated,
  setTags,
  setUgtLocale,
  tags,
  ugtLocale,
}) => {
  const [queryString, setQueryString] = useState('');
  const [sortBy, setSortBy] = useState<TagSortByOptions>('name');
  const [pagesNeedRender, setPagesNeedRender] = useState(false);

  const tagCount = (loading || !tags) ? 0 : Object.keys(tags).length;
  const pageSize = getPageSize(tagCount);
  const sortedTags = tags ? sortedNames(tags, queryString, sortBy) : null;

  const sortingOptions = loading ? null : (
    <div className="sorting-options">
      <SearchTextfield
        id="tag-search"
        label={bfTranslate('Search tags')}
        onChange={(e): void => setQueryString(e.target.value)}
        placeholder={`${bfTranslate('Search')} ${tagCount} ${bfTranslate('tags')}`}
        showLabel={false}
      />
      <div className="sorting-options-container-right">
        <BulkCustomFieldsLanguageMenuDropdown
          setUgtLocale={setUgtLocale}
          ugtLocale={ugtLocale}
        />
        <div className="sort-options">
          <div><p>{bfTranslate('Sort by')}:</p></div>
          <ListDropdown
            className="select-tag-sorting"
            onChange={(selected): void => setSortBy(selected.value as TagSortByOptions)}
            options={[
              { value: 'name', label: bfTranslate('Name') },
              { value: 'assetCount', label: bfTranslate('Asset Count') }]}
            searchable={false}
            value={sortBy}
            virtualizeOptions={false}
          />
        </div>
      </div>
    </div>
  );

  const updateTags = async (oldName, newName): Promise<void> => {
    const body = {
      data: {
        manage_tags: {
          management_action: newName ? 'update' : 'delete',
          brandfolder_key: BFG.resource?.key,
          old_name: oldName,
          new_name: newName,
          auto_generated: autoGenerated,
        }
      }
    };

    await fetchJson({
      url: '/api/v4/bulk_actions/assets/manage_tags',
      method: 'PUT',
      params: {
        ugt_locale: ugtLocale
      },
      body
    });

    updateVisibleTags(tags, setTags, oldName, newName);
    setPagesNeedRender(true);
  };

  const renderRows = (pageIndex: number): ReactNode => {
    const tagPage = getPageSlice(pageIndex, pageSize, sortedTags);
    return tagPage.map((tagName) => (
      <div key={tagName} className="tag-row">
        <TagsRow
          autoGenerated={autoGenerated}
          count={tags[tagName]}
          name={tagName}
          ugtLocale={ugtLocale}
          updateTags={updateTags}
        />
      </div>
    ));
  };

  const renderPages = (): ReactNode => {
    const numPages = getNumPages(pageSize, sortedTags?.length);
    const renderedPages = [];
    for (let pageIndex = 0; pageIndex < numPages; pageIndex += 1) {
      renderedPages.push(
        <RowPage
          key={`tag-row-page-${pageIndex}`}
          isInitialPage={pageIndex === 0}
          pageSize={pageSize}
          renderRows={(): ReactNode => renderRows(pageIndex)}
          rerender={pagesNeedRender}
          rowHeight={71}
          setRerender={setPagesNeedRender}
        />
      );
    }
    return <>{renderedPages}</>;
  };

  return (
    <div className="manage-tags">
      <h2>{BFG.resource?.name ? `${bfTranslate('Tags for')} ${decode(BFG.resource.name)}` : bfTranslate('Tags')}</h2>
      <hr />
      {sortingOptions}
      {!(BFG.hasFeature('disable_google_vision_autotagging') || !BFG.brandfolderSettings?.auto_tagging) && (
        <div className="filter-by-auto-generated-tags">
          <StandardSwitch
            isChecked={autoGenerated}
            onChange={(): void => setAutoGenerated(!autoGenerated)}
          >
            {bfTranslate('Show auto-generated tags')}
          </StandardSwitch>
        </div>
      )}
      {error && (
        <StandardAlert
          look={AlertLooks.Error}
        >
          {genericError}
        </StandardAlert>
      )}
      {!error && (
        <>
          <div className="headers">
            <div className="headers-container">
              <h3 className="quarter-width">{bfTranslate('Tags')}</h3>
              <h3 className="fifteen-width"># {bfTranslate('Assets')}</h3>
            </div>
          </div>
          <div className="tags-list">
          {loading ? (
            <CircleLoader
              className="manage-tags__page-loader"
              label={bfTranslate('Loading tags')}
            />
          ) : renderPages()}
          </div>
        </>
      )}
    </div>
  );
};